// useAuth 훅 — 인증 함수 제공 (signIn/signUp/signOut)
// 세션 초기화는 App.tsx AuthInitializer에서 처리 (중복 방지)
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/store/authStore'
import { useProfileStore } from '@/store/profileStore'
import { apiFetch } from '@/lib/api'
import { setSentryUser } from '@/lib/sentry'
import { clearApiCache } from '@/hooks/usePWA'

export function useAuth() {
  const navigate = useNavigate()
  const { user, session, storeInfo, isOnboarded, reset } = useAuthStore()

  // 이메일/비번 회원가입 (동의 메타데이터 포함)
  const signUp = async (email: string, password: string, metadata?: Record<string, unknown>) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: metadata ? { data: metadata } : undefined,
    })
    if (error) throw error
    // G4: 환영 메일 trigger (실패해도 무시 — 가입 흐름 막지 않음)
    if (data.user?.id) {
      void apiFetch('/api/v1/notification-cron/welcome', {
        method: 'POST',
        body: JSON.stringify({ store_id: data.user.id }),
      }).catch(() => {})
    }
    return data
  }

  // 이메일/비번 로그인
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
    return data
  }

  // 로그아웃 — Critical C3: authStore + profileStore 동시 reset
  // Bonus E6: SW API 캐시 비우기 (다음 사용자 데이터 누출 방지)
  // Launch A5: Sentry 사용자 컨텍스트 클리어
  const signOut = async () => {
    await supabase.auth.signOut()
    reset()
    useProfileStore.getState().reset()
    setSentryUser(null)
    void clearApiCache()
    navigate('/login')
  }

  return {
    user,
    session,
    storeInfo,
    isOnboarded,
    isAuthenticated: !!user,
    signUp,
    signIn,
    signOut,
    setIsOnboarded: useAuthStore.getState().setIsOnboarded,
    setStoreInfo: useAuthStore.getState().setStoreInfo,
  }
}
